import { createTheme } from "@mui/material/styles";

export const HAMASPIK_FONT_DISPLAY = '"Inter", sans-serif';
export const HAMASPIK_FONT_BODY = '"Inter", sans-serif';


export const HAMASPIK_CONTROL_HEIGHT = 36;

export const HAMASPIK_LABEL_SX = {
  fontFamily: HAMASPIK_FONT_BODY,
  fontSize: "12px",
  fontWeight: 600,
  color: "#592627",
  lineHeight: 1.3,
};

export const HAMASPIK_COLORS = {
  orange50: "#FEF2EB",
  orange100: "#FCDFD0",
  orange200: "#FAC6A9",
  orange300: "#F8A87E",
  orange500: "#F57F43",
  orange600: "#E2682B",
  orange700: "#BC5423",
  brown50: "#F7EFEF",
  brown300: "#A97274",
  brown500: "#592627",
  brown600: "#4A2021",
  brown700: "#3B1A1A",
  cream: "#FBF6F1",
  neutral0: "#FFFFFF",
  neutral50: "#FAF7F4",
  neutral100: "#F2ECE6",
  neutral200: "#E5DACF",
  neutral300: "#D2C3B5",
  neutral400: "#AE9C8D",
  neutral500: "#897868",
  neutral600: "#6A5B4E",
  neutral700: "#4E423A",
  neutral800: "#332C27",
  neutral900: "#211C18",
  success50: "#E9F4ED",
  success500: "#3F9D6B",
  success700: "#2C6F4B",
  warning50: "#FCF1DD",
  warning500: "#E29515",
  warning700: "#A56A0C",
  danger50: "#FBE9E6",
  danger500: "#C7402E",
  danger700: "#8F2A1D",
  info50: "#E8EFF4",
  info500: "#3E6B8A",
  info700: "#2A4C63",
  heading: "#592627",
  textMuted: "#6A5B4E",
  textSubtle: "#897868",
  shadowFocus: "0 0 0 3px rgba(245,127,67,0.40)",
};

export const hamaspikTheme = createTheme({
  palette: {
    primary: { main: HAMASPIK_COLORS.orange500, light: HAMASPIK_COLORS.orange300, dark: HAMASPIK_COLORS.orange700, contrastText: "#fff" },
    secondary: { main: HAMASPIK_COLORS.brown500, light: HAMASPIK_COLORS.brown300, dark: HAMASPIK_COLORS.brown700, contrastText: "#fff" },
    success: { main: HAMASPIK_COLORS.success500, light: HAMASPIK_COLORS.success50, dark: HAMASPIK_COLORS.success700, contrastText: "#fff" },
    warning: { main: HAMASPIK_COLORS.warning500, light: HAMASPIK_COLORS.warning50, dark: HAMASPIK_COLORS.warning700, contrastText: "#fff" },
    error: { main: HAMASPIK_COLORS.danger500, light: HAMASPIK_COLORS.danger50, dark: HAMASPIK_COLORS.danger700, contrastText: "#fff" },
    info: { main: HAMASPIK_COLORS.info50, dark: HAMASPIK_COLORS.info700, contrastText: HAMASPIK_COLORS.info700 },
    background: { default: HAMASPIK_COLORS.cream, paper: HAMASPIK_COLORS.neutral0 },
    text: { primary: HAMASPIK_COLORS.neutral900, secondary: HAMASPIK_COLORS.textMuted, disabled: HAMASPIK_COLORS.neutral400 },
    divider: HAMASPIK_COLORS.neutral200,
    grey: {
      50: HAMASPIK_COLORS.neutral50,
      100: HAMASPIK_COLORS.neutral100,
      200: HAMASPIK_COLORS.neutral200,
      300: HAMASPIK_COLORS.neutral300,
      400: HAMASPIK_COLORS.neutral400,
      500: HAMASPIK_COLORS.neutral500,
      600: HAMASPIK_COLORS.neutral600,
      700: HAMASPIK_COLORS.neutral700,
      800: HAMASPIK_COLORS.neutral800,
      900: HAMASPIK_COLORS.neutral900,
    },
  },
  typography: {
    fontFamily: HAMASPIK_FONT_BODY,
    button: { textTransform: "none", fontWeight: 700 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
          lineHeight: 1,
          border: "2px solid transparent",
          boxShadow: "none",
          // The design system's own Button never constrains width — it's exactly
          // padding + content. MUI's built-in 64px default makes short labels like
          // "Yes"/"No" render as oversized blocks; drop it everywhere.
          minWidth: "auto",
          "&:hover": { boxShadow: "none" },
        },
        sizeSmall: { padding: "7px 14px", fontSize: 13, minHeight: 32 },
        sizeMedium: { padding: "9px 18px", fontSize: 14 },
        sizeLarge: { padding: "12px 26px", fontSize: 15 },
        outlined: {
          borderWidth: 2,
          borderColor: HAMASPIK_COLORS.orange500,
          color: HAMASPIK_COLORS.orange700,
          "&:hover": { borderWidth: 2, backgroundColor: HAMASPIK_COLORS.orange50 },
        },
        outlinedPrimary: {
          borderColor: HAMASPIK_COLORS.orange500,
          color: HAMASPIK_COLORS.orange700,
          "&:hover": { borderWidth: 2, backgroundColor: HAMASPIK_COLORS.orange50 },
        },
        outlinedSecondary: {
          borderColor: HAMASPIK_COLORS.brown500,
          color: HAMASPIK_COLORS.brown500,
          "&:hover": { borderWidth: 2, backgroundColor: HAMASPIK_COLORS.brown50 },
        },
        text: {
          color: HAMASPIK_COLORS.heading,
          "&:hover": { backgroundColor: HAMASPIK_COLORS.neutral100 },
        },
        contained: { "&:hover": { backgroundColor: HAMASPIK_COLORS.orange600 } },
        containedSecondary: { "&:hover": { backgroundColor: HAMASPIK_COLORS.brown600 } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: HAMASPIK_COLORS.neutral0,
          minHeight: HAMASPIK_CONTROL_HEIGHT,
          // Must match the `input` font size below. A text field's height comes from
          // `height: 1.4375em` on the input itself, but a Select's display element is a div sized
          // by the line-height MUI declares here — inherited as a computed px. Leaving the root at
          // 1rem while shrinking only the input resolved that to 23px against the input's 18.7px,
          // so every dropdown rendered ~4px taller than the fields beside it.
          fontSize: 13,
          // A picker's open button is an un-sized (medium, 40px) IconButton. With the trimmed
          // `input` padding below that is taller than the control, so every date field came out
          // ~5px taller than the inputs next to it. Small-button padding keeps it under the height.
          "& .MuiInputAdornment-root .MuiIconButton-root": { padding: 5 },
          // MUI puts the padding on the root for multiline and zeroes the textarea's — the `input`
          // override below re-adds it, which double-insets notes fields. Match the single-line
          // inset here and drop it from the textarea.
          "&.MuiInputBase-multiline": { minHeight: 0, padding: "8px 12px" },
          "& fieldset": { borderWidth: 1.5, borderColor: HAMASPIK_COLORS.neutral200 },
          "&:hover fieldset": { borderColor: HAMASPIK_COLORS.neutral300 },
          "&.Mui-focused fieldset": { borderWidth: 1.5, borderColor: HAMASPIK_COLORS.orange500 },
          "&.Mui-focused": { boxShadow: HAMASPIK_COLORS.shadowFocus },
          "&.Mui-disabled": {
            backgroundColor: HAMASPIK_COLORS.neutral100,
            "& fieldset": { borderColor: HAMASPIK_COLORS.neutral200 },
          },
        },
        input: {
          fontFamily: HAMASPIK_FONT_BODY,
          
          fontSize: 13,
          color: HAMASPIK_COLORS.neutral900,
          padding: "8px 12px",
          "&.Mui-disabled": {
            color: HAMASPIK_COLORS.textMuted,
            WebkitTextFillColor: HAMASPIK_COLORS.textMuted,
            opacity: 1,
          },
        },
        inputMultiline: { padding: 0 },
      },
    },
    MuiFormLabel: { styleOverrides: { root: { fontFamily: HAMASPIK_FONT_BODY, fontSize: 13, fontWeight: 600, color: HAMASPIK_COLORS.heading } } },
    MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 700 } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});
